import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { orderViewsActions } from "../../../_store/orderView.slice";
import { ordersActions } from "../../../_store/order.slice";
import { OrderCalendarFilterBox } from "../../../_components/OrderViews/OrderCalendar/OrderCalendarFilterBasic";
import { useCalendarView } from "../../../_components/OrderViews/OrderCalendar/useCalendarView";
import { LoadingSpinner } from "../../../_components/Abstractions/LoadingSpinner";
import { Card, Table } from "react-bootstrap";
import { BadgeDomain } from "../../../_components/Abstractions/BadgeDomain";
import { WeighbridgeTicketsToOrderForm } from "./form/WeighbridgeTicketsToOrderForm";
import { DomainPagination } from "../../../_components/Abstractions/DomainPagination";
import { useNavigate } from "react-router-dom";
import { OrderStatusBadge } from "../../../_components/OrderViews/Components/OrderStatusBadge";
import { TransactionTypeBadge } from "../../../_components/OrderViews/Components/TransactionTypeBadge";
import { ORDER_STATUS } from "../../../domain/Orders/OrderStatus";
import { mapWeighbridgeTicketsPayload } from "./form/mapWeighbridgeTicketsPayload";

export function OrderWeighbridgeTicketsManagement() {
  const { weighbridgeTicketsManagementView } = useSelector((x) => x.orderViews);
  const { items, paging, loading } = weighbridgeTicketsManagementView;
  const { refreshCalendar, currentFilters, handleCalendarChange, refreshFilters, setCurrentFilters } =
    useCalendarView({ skipDates: true, orderViewsAction: orderViewsActions.getWeighbridgeTicketsManagementView });

  const handlePageChange = async (pageNumber, pageSize) => {
    const newPaging = {
      pageNumber: pageNumber ?? paging?.pageNumber,
      pageSize: pageSize ?? paging?.pageSize,
    };
    await refreshFilters(newPaging);
  };

  useEffect(() => {
    const defaultPaging = {
      pageNumber: paging?.pageNumber ?? 1,
      pageSize: paging?.pageSize ?? 50,
      isProtocolNeeded: true,
      isProtocolCompleted: false
    };
    refreshFilters(defaultPaging);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mb-4">
      <h2>Zarządzanie kwitami wagowymi</h2>
      <OrderCalendarFilterBox
        currentDate={null}
        currentFilters={currentFilters}
        handleCalendarChange={handleCalendarChange}
        handleFilterChange={refreshFilters}
        refreshCalendar={refreshCalendar}
        setCurrentFilters={setCurrentFilters}
        isCalendarView={false}
      />
      <LoadingSpinner isLoading={loading} as="div" />
      <WeighbridgeTicketsManagementTable
        items={items}
        paging={paging}
        loading={loading}
        handlePageChange={handlePageChange}
        refreshList={() => refreshFilters({ pageNumber: paging?.pageNumber ?? 1, pageSize: paging?.pageSize ?? 50, isProtocolNeeded: true, isProtocolCompleted: false })}
      />
    </div>
  );
}

function WeighbridgeTicketsManagementTable({ items, loading, paging, handlePageChange, refreshList }) {
  const navigate = useNavigate();

  if (loading) {
    return;
  }

  if (!items?.length) {
    return <p className="text-muted mt-3">Brak zleceń.</p>;
  }

  return (
    <div className="d-flex flex-column">
      <Card>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Nr zlecenia</th>
                <th>Kontrahent</th>
                <th>Adres</th>
                <th>Data transportu</th>
                <th>Typ</th>
                <th>Status</th>
                <th>Kwity wagowe</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {items.map((order) => (
                <WeighbridgeTicketsManagementRow
                  key={order.id}
                  order={order}
                  navigate={navigate}
                  refreshList={refreshList}
                />
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      {paging && (
        <DomainPagination
          onPageChange={handlePageChange}
          paging={paging}
          itemsCount={items.length}
        />
      )}
    </div>
  );
}

function WeighbridgeTicketsManagementRow({ order, navigate, refreshList }) {
  const {
    id,
    orderNumber,
    contractorName,
    addressName,
    orderDate,
    transactionType,
    status,
    weighbridgeTicketNumbers,
  } = order;

  const weighbridgeTicketsCount = weighbridgeTicketNumbers?.length ?? 0;

  return (
    <tr>
      <td>
        <span
          className="text-primary hover-pointer text-decoration-underline"
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/orders/${id}`)}
        >
          {orderNumber}
        </span>
      </td>
      <td>{contractorName}</td>
      <td>{addressName}</td>
      <td>{orderDate}</td>
      <td>
        <TransactionTypeBadge transactionType={transactionType} size="smaller" />
      </td>
      <td>
        <OrderStatusBadge status={status} size="smaller" />
      </td>
      <td>
        {weighbridgeTicketsCount > 0 ? (
          <BadgeDomain bg="success">{weighbridgeTicketsCount}</BadgeDomain>
        ) : (
          <BadgeDomain bg="secondary">0</BadgeDomain>
        )}
      </td>
      <td>
        <AttachWeighbridgeTicketsInManagementView order={order} orderId={id} refreshList={refreshList} />
      </td>
    </tr>
  );
}

function AttachWeighbridgeTicketsInManagementView({ order, orderId, refreshList }) {
  const dispatch = useDispatch();
  const [loadedWastesDetails, setLoadedWastesDetails] = useState(null);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { status } = order || {};

  if (status === ORDER_STATUS.CLOSED || status === ORDER_STATUS.CANCELLED) {
    return null;
  }

  const handleOpenClick = async () => {
    setLoadingTickets(true);
    const response = await dispatch(orderViewsActions.getWastesDetails({ id: orderId }));
    if (!response?.error) {
      setLoadedWastesDetails(response.payload);
    }
    setLoadingTickets(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setLoadedWastesDetails(null);
  };

  const orderWithTickets = loadedWastesDetails
    ? { ...order, wastesDetails: loadedWastesDetails }
    : order;

  const weighbridgeTicketsCount = loadedWastesDetails?.weighbridgeTickets?.length
    ?? order?.wastesDetails?.weighbridgeTickets?.length;

  const onSubmit = async (payload) => {
    const weighbridgeTickets = mapWeighbridgeTicketsPayload(payload.weighbridgeTickets, {
      forceProtocolCompleted: true,
    });
    const requestPayload = {
      orderId,
      weighbridgeTickets,
      overrideWasteDetails: payload.overrideWasteDetails,
      bypassTicketDuplicationValidation: payload.bypassTicketDuplicationValidation,
    };
    const response = await dispatch(ordersActions.attachWeighbridgeTickets({ orderId, payload: requestPayload }));
    if (!response?.error) {
      handleCloseModal();
      refreshList();
    }
    return response;
  };

  return (
    <WeighbridgeTicketsToOrderForm
      key={showModal ? (loadedWastesDetails ? "loaded" : "loading") : "closed"}
      order={orderWithTickets}
      onSubmit={onSubmit}
      show={showModal}
      onOpenClick={handleOpenClick}
      onCloseModal={handleCloseModal}
      loading={loadingTickets}
      weighbridgeTicketsCount={weighbridgeTicketsCount}
      setProtocolCompleted
    />
  );
}
